import './scheduleMobile.css';
import ResponsiveRow from '../shared/ResponsiveRow';
import ModalSurface from '../shared/ModalSurface';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import useModuleAccess from '../../hooks/useModuleAccess';
import EngagementFormModal from '../engagements/EngagementFormModal';
import useTripPriceAccess from '../../hooks/useTripPriceAccess';
import { excludeEventRecords } from '../../lib/eventEngagement';
import FlightSuggestionCard from '../dispatch/FlightSuggestionCard';
import { loadFlightDetails, saveTripFlight } from '../../lib/tripFlight';
import HoverHelp from '../shared/HoverHelp';
import { BTN, TILE, standardPillClass } from '../ui/platformStandard';

const DRIVER_COLORS = ['#2563EB', '#7C3AED', '#DB2777', '#0891B2', '#D97706', '#16A34A', '#DC2626'];
const SERVICE_COLORS = ['#0F766E', '#7C2D12', '#4338CA', '#BE123C', '#A16207', '#4F46E5'];

const dateKey = (date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
].join('-');

const sameDay = (left, right) => dateKey(left) === dateKey(right);

const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const startOfWeek = (date, firstDayOfWeek) => {
    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const firstDayOffset = firstDayOfWeek === 'Monday' ? 1 : 0;
    let daysToSubtract = start.getDay() - firstDayOffset;
    if (daysToSubtract < 0) daysToSubtract += 7;
    start.setDate(start.getDate() - daysToSubtract);
    return start;
};

const getWeekNumber = (date) => {
    const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNumber = utcDate.getUTCDay() || 7;
    utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayNumber);
    const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
    return Math.ceil((((utcDate - yearStart) / 86400000) + 1) / 7);
};

const minutesSinceMidnight = (date) => date.getHours() * 60 + date.getMinutes();
const DEFAULT_DAY_START_MINUTES = 6 * 60;

const parseCalendarStartMinutes = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Math.max(0, Math.min(23 * 60, Math.floor(value)));
    }
    if (typeof value === 'string') {
        const match = value.trim().match(/^(\d{1,2})(?::(\d{2}))?$/);
        if (!match) return null;
        const hours = Number(match[1]);
        const minutes = Number(match[2] || 0);
        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
        return hours * 60 + minutes;
    }
    return null;
};

function ScheduleCalendarView({ theme = 'light', accountSettings = {}, userId = null, scheduleCalendarLabel = 'Calendar', isUser = false }) {
  const [phone, setPhone] = useState(() => Boolean(window.matchMedia?.('(max-width: 767px)').matches));
    const [touch, setTouch] = useState(() => Boolean(window.matchMedia?.('(pointer: coarse)').matches));
    const [view, setView] = useState(() => window.matchMedia?.('(max-width: 767px)').matches ? 'day' : 'week');
    const [selectedEvent, setSelectedEvent] = useState(null);
    // FA-2: the selected trip's flight line and pickup suggestion
    const [selectedFlight, setSelectedFlight] = useState(null);
    useEffect(() => {
        const media = window.matchMedia?.('(max-width: 767px)');
        const pointer = window.matchMedia?.('(pointer: coarse)');
        const sync = () => { setPhone(media.matches); setTouch(Boolean(pointer?.matches)); };
        media?.addEventListener?.('change', sync);
        pointer?.addEventListener?.('change', sync);
        return () => { media?.removeEventListener?.('change', sync); pointer?.removeEventListener?.('change', sync); };
    }, []);
    const [currentDate, setCurrentDate] = useState(() => new Date());
    const [driver, setDriver] = useState('all');
    const [entity, setEntity] = useState('all');
    const [schedules, setSchedules] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [entities, setEntities] = useState([]);
    const [error, setError] = useState(null);
    const [engagements, setEngagements] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ schedule_type: 'booking', driver_id: '', confirmation_number: '', scheduled_start: '', scheduled_end: '', notes: '' });
    const [showEngagementForm, setShowEngagementForm] = useState(false);
    const [engagementFormPrefill, setEngagementFormPrefill] = useState({ initialDate: null, initialTime: null, initialEntityId: null });
    const { allowed: canManage, loading: accessLoading } = useModuleAccess('dispatch', isUser ? 'edit' : 'manage');
    const canShowTripPrices = useTripPriceAccess(isUser);
    const calendarSettings = {
        timeScale: 30,
        firstDayOfWeek: 'Sunday',
        showWeekNumbers: false,
        useDynamicColumnWidths: true,
        timeFormat: '12hr',
        colorBy: 'staff',
        ...accountSettings.calendar
    };
    const calendarDayStartMinutes = parseCalendarStartMinutes(
        calendarSettings.startTime
        ?? calendarSettings.dayStartTime
        ?? calendarSettings.workDayStart
        ?? calendarSettings.workdayStart
    ) ?? DEFAULT_DAY_START_MINUTES;

    const load = useCallback(async () => {
        setError(null);
        const [scheduleResult, driverResult, entityResult, engagementResult] = await Promise.all([
            supabaseClient.from('schedule').select('schedule_id,confirmation_number,driver_id,scheduled_start,scheduled_end,status,schedule_type,notes,entity_id,conflict_with,external_calendar_conflict,engagements!schedule_confirmation_number_fkey(id,entity_id,service_type)').order('scheduled_start'),
            supabaseClient.rpc('schedule_driver_directory'),
            supabaseClient.from('entities').select('id,name,entity_code').order('entity_code'),
            supabaseClient.from('engagements').select('confirmation_number,entity_id,service_type,event_engagement_details(engagement_id)').eq('status', 'confirmed').order('confirmation_number')
        ]);
        const firstError = scheduleResult.error || driverResult.error || entityResult.error || engagementResult.error;
        if (firstError) {
            setError(firstError.message);
            return;
        }
        setSchedules(scheduleResult.data || []);
        setDrivers(driverResult.data || []);
        setEntities(entityResult.data || []);
        setEngagements(excludeEventRecords(engagementResult.data));
    }, []);

    useEffect(() => { load(); }, [load]);

    const selectedEngagementId = (() => {
        const linked = selectedEvent?.engagements;
        return (Array.isArray(linked) ? linked[0]?.id : linked?.id) || null;
    })();
    const loadSelectedFlight = useCallback(async () => {
        if (!selectedEngagementId) { setSelectedFlight(null); return; }
        const flights = await loadFlightDetails(supabaseClient, [selectedEngagementId]);
        setSelectedFlight(flights[selectedEngagementId] || null);
    }, [selectedEngagementId]);
    useEffect(() => { loadSelectedFlight(); }, [loadSelectedFlight]);

    const driversById = useMemo(() => new Map(drivers.map((item, index) => [item.id, {
        ...item,
        color: DRIVER_COLORS[index % DRIVER_COLORS.length]
    }])), [drivers]);
    const servicesByName = useMemo(() => new Map((accountSettings.services || []).map((service, index) => [
        service.name,
        service.color || SERVICE_COLORS[index % SERVICE_COLORS.length]
    ])), [accountSettings.services]);
    const driverLabel = (item) => item?.full_name?.trim() || item?.id || 'Unassigned driver';
    const visible = useMemo(() => schedules.filter((item) =>
        (driver === 'all' || item.driver_id === driver)
        && (entity === 'all' || item.entity_id === entity || item.engagements?.entity_id === entity)
    ), [schedules, driver, entity]);

    const formatTime = (date) => date.toLocaleTimeString([], calendarSettings.timeFormat === '24hr'
        ? { hour: '2-digit', minute: '2-digit', hour12: false }
        : { hour: 'numeric', minute: '2-digit' });
    const formatDateTime = (date) => `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${formatTime(date)}`;
    const scheduleTitle = (item) => item.schedule_type === 'block_off'
        ? 'Block Off Time'
        : item.engagements?.service_type || item.confirmation_number || 'Booking';
    const scheduleColor = (item) => {
        if (item.schedule_type === 'block_off') return '#4B5563';
        if (calendarSettings.colorBy === 'service') {
            return servicesByName.get(item.engagements?.service_type) || SERVICE_COLORS[Math.abs((item.engagements?.service_type || item.confirmation_number || '').length) % SERVICE_COLORS.length];
        }
        return driversById.get(item.driver_id)?.color || DRIVER_COLORS[0];
    };
    const isConflict = (item) => item.external_calendar_conflict || (item.conflict_with || []).length > 0 || item.status === 'conflict';
    const isDark = theme === 'dark';
    const scheduleStyle = (item, compact = false) => {
        if (item.schedule_type === 'block_off') {
            return {
                backgroundColor: isDark ? '#374151' : '#E5E7EB',
                borderLeft: `3px solid ${scheduleColor(item)}`,
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(75, 85, 99, 0.16) 4px, rgba(75, 85, 99, 0.16) 8px)'
            };
        }
        const color = scheduleColor(item);
        return {
            backgroundColor: compact ? `${color}1F` : `${color}24`,
            borderLeft: `3px solid ${color}`
        };
    };

    const createSchedule = async () => {
        setError(null);
        const selectedDriver = drivers.find((item) => item.id === form.driver_id);
        if (!selectedDriver || !form.scheduled_start || !form.scheduled_end) {
            setError('Choose a driver and time window.');
            return;
        }
        if (form.schedule_type === 'booking' && !form.confirmation_number) {
            setError('Choose a confirmed engagement for a booking.');
            return;
        }
        const selectedEngagement = engagements.find((item) => item.confirmation_number === form.confirmation_number);
        const { error: createError } = await supabaseClient.from('schedule').insert([{
            org_id: selectedDriver.org_id,
            driver_id: selectedDriver.id,
            schedule_type: form.schedule_type,
            confirmation_number: form.schedule_type === 'booking' ? form.confirmation_number : null,
            entity_id: form.schedule_type === 'booking' ? selectedEngagement?.entity_id : selectedDriver.entity_id,
            scheduled_start: new Date(form.scheduled_start).toISOString(),
            scheduled_end: new Date(form.scheduled_end).toISOString(),
            status: 'held',
            notes: form.notes || null
        }]);
        if (createError) {
            setError(createError.message);
            return;
        }
        setShowCreate(false);
        setForm({ schedule_type: 'booking', driver_id: '', confirmation_number: '', scheduled_start: '', scheduled_end: '', notes: '' });
        load();
    };

    const createEngagementFromCalendar = async (payload) => {
        // FA-2: the flight goes to the trip's structured flight fields, not the engagements row
        const { flight, ...engagementPayload } = payload;
        const { data: created, error: insertError } = await supabaseClient
            .from('engagements')
            .insert([{ ...engagementPayload, status: 'requested', created_by: userId, channel: 'web' }])
            .select('id')
            .single();
        if (insertError) throw insertError;
        if (flight && created?.id) await saveTripFlight(supabaseClient, created.id, flight);
        setShowEngagementForm(false);
        load();
    };

    const openEngagementForm = (date, time, entityId) => {
        setEngagementFormPrefill({
            initialDate: dateKey(date),
            initialTime: time || null,
            initialEntityId: entityId || null
        });
        setShowEngagementForm(true);
    };

    const navigate = (direction) => {
        const amount = view === 'month' ? 30 : view === 'week' ? 7 : 1;
        setCurrentDate((date) => addDays(date, amount * direction));
    };
    const dateRangeLabel = view === 'month'
        ? currentDate.toLocaleDateString([], { month: 'long', year: 'numeric' })
        : view === 'week'
            ? `Week of ${startOfWeek(currentDate, calendarSettings.firstDayOfWeek).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`
            : currentDate.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    const gridColumns = (days) => calendarSettings.useDynamicColumnWidths
        ? `64px repeat(${days}, minmax(132px, 1fr))`
        : `64px repeat(${days}, 168px)`;

    const ScheduleBlock = ({ item, top, height, compact = false }) => {
        const start = new Date(item.scheduled_start);
        const end = new Date(item.scheduled_end);
        const driverName = driverLabel(driversById.get(item.driver_id));
        return <div
            role={phone || touch ? "button" : undefined} tabIndex={phone || touch ? 0 : undefined}
            onClick={phone || touch ? (event) => { event.stopPropagation(); setSelectedEvent(item); } : undefined}
            onKeyDown={phone || touch ? (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); event.stopPropagation(); setSelectedEvent(item); } } : undefined}
            className={`ia-schedule-event overflow-hidden rounded p-1 shadow-sm ${compact ? 'text-[10px]' : 'text-xs'}`}
            style={{ ...scheduleStyle(item, compact), ...(top === undefined ? {} : { position: 'absolute', top, height, left: 4, right: 4 }) }}
            title={`${scheduleTitle(item)} — ${driverName}`}
        >
            <div className="font-semibold truncate">{item.schedule_type === 'block_off' && '🚫 '}{scheduleTitle(item)}</div>
            <div className="truncate">{formatTime(start)}–{formatTime(end)} · {driverName}</div>
            {!compact && <div className={`truncate ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{item.status}{isConflict(item) ? ' · Conflict' : ''}</div>}
        </div>;
    };

    const TimeGrid = ({ days }) => {
        const pixelsPerMinute = calendarSettings.timeScale === 15 ? 1 : 0.75;
        const dayStartMinutes = Math.min(
            24 * 60 - calendarSettings.timeScale,
            Math.floor(calendarDayStartMinutes / calendarSettings.timeScale) * calendarSettings.timeScale
        );
        const height = (24 * 60 - dayStartMinutes) * pixelsPerMinute;
        const slots = [];
        for (let minute = dayStartMinutes; minute < 24 * 60; minute += calendarSettings.timeScale) {
            slots.push(minute);
        }
        const eventsForDay = (day) => visible.filter((item) => sameDay(new Date(item.scheduled_start), day));
        // UX pass 2026-10-25 (Val: "The time in the calendar should freeze frame at and scroll up into the date row"):
        // the grid scrolls inside its own box, so the date row stays frozen on top and the hours slide up under it;
        // the time column stays frozen on the left when a wide week scrolls sideways.
        return <div className={`${TILE} ia-tile3d--clip pt-[5px]`}><div className="ia-calendar-scroll ia-calendar-scroll--frozen overflow-auto" data-testid="calendar-time-grid">
            <div className="min-w-max" style={{ minWidth: calendarSettings.useDynamicColumnWidths ? undefined : `${64 + days.length * 168}px` }}>
                <div className={`ia-calendar-date-row sticky top-0 z-20 grid border-b ${isDark ? 'border-gray-700 bg-gray-800' : 'bg-gray-50'}`} style={{ gridTemplateColumns: gridColumns(days.length) }} data-testid="calendar-date-row">
                    <div className={`ia-calendar-time ia-calendar-corner p-2 text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>TIME</div>
                    {days.map((day) => <div key={dateKey(day)} className={`border-l p-2 text-center ${isDark ? 'border-gray-700' : ''}`}>
                        <div className={`text-xs font-semibold ${isDark ? 'text-gray-200' : ''}`}>{day.toLocaleDateString([], { weekday: 'short' })}</div>
                        <div className={`text-sm ${isDark ? 'text-gray-300' : ''}`}>{day.toLocaleDateString([], { month: 'short', day: 'numeric' })}</div>
                    </div>)}
                </div>
                <div className="grid" style={{ gridTemplateColumns: gridColumns(days.length) }}>
                    <div className="ia-calendar-time ia-calendar-gutter relative border-r" style={{ height }} data-testid="calendar-time-gutter">
                        {slots.map((minute) => <div key={minute} className={`absolute w-full pr-2 text-right text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`} style={{ top: (minute - dayStartMinutes) * pixelsPerMinute }}>
                            {minute % 60 === 0 && formatTime(new Date(2000, 0, 1, minute / 60))}
                        </div>)}
                    </div>
                    {days.map((day) => <div key={dateKey(day)} className={`relative border-r cursor-pointer ${isDark ? 'border-gray-700' : ''}`} style={{ height }} onClick={(e) => {
                        // Ignore clicks that land on an event block rather than empty space.
                        if (e.target !== e.currentTarget) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const rawMinutes = dayStartMinutes + Math.floor((e.clientY - rect.top) / pixelsPerMinute);
                        const snapped = Math.floor(rawMinutes / calendarSettings.timeScale) * calendarSettings.timeScale;
                        const h = String(Math.floor(snapped / 60) % 24).padStart(2, '0');
                        const m = String(snapped % 60).padStart(2, '0');
                        openEngagementForm(day, `${h}:${m}`, entity !== 'all' ? entity : null);
                    }}>
                        {slots.map((minute) => <div key={minute} className="absolute w-full border-t" style={{
                            top: (minute - dayStartMinutes) * pixelsPerMinute,
                            borderColor: isDark
                                ? (minute % 60 === 0 ? '#4B5563' : '#1F2937')
                                : (minute % 60 === 0 ? '#D1D5DB' : '#F3F4F6')
                        }} />)}
                        {eventsForDay(day).map((item) => {
                            const start = new Date(item.scheduled_start);
                            const end = new Date(item.scheduled_end);
                            const startMinute = minutesSinceMidnight(start);
                            const durationMinutes = Math.max(0, Math.round((end - start) / 60000));
                            const endMinute = startMinute + durationMinutes;
                            const clippedStart = Math.max(dayStartMinutes, startMinute);
                            const clippedEnd = Math.min(24 * 60, endMinute);
                            if (clippedEnd <= clippedStart) return null;
                            const top = Math.max(0, (clippedStart - dayStartMinutes) * pixelsPerMinute);
                            const duration = clippedEnd - clippedStart;
                            const renderDuration = Math.max(calendarSettings.timeScale, duration);
                            return <ScheduleBlock key={item.schedule_id} item={item} top={top} height={Math.max(24, renderDuration * pixelsPerMinute)} />;
                        })}
                    </div>)}
                </div>
            </div>
        </div></div>;
    };

    const MonthGrid = () => {
        const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const start = startOfWeek(firstDay, calendarSettings.firstDayOfWeek);
        const weeks = Array.from({ length: 6 }, (_, week) => Array.from({ length: 7 }, (_, day) => addDays(start, week * 7 + day)));
        const dayNames = weeks[0].map((day) => day.toLocaleDateString([], { weekday: 'short' }));
        const columnTemplate = calendarSettings.showWeekNumbers ? '40px repeat(7, minmax(120px, 1fr))' : 'repeat(7, minmax(120px, 1fr))';
        return <div className={`${TILE} ia-tile3d--clip pt-[5px]`}><div className="ia-calendar-scroll overflow-auto">
            <div className="min-w-[840px]">
                <div className={`grid border-b ${isDark ? 'border-gray-700 bg-gray-800' : 'bg-gray-50'}`} style={{ gridTemplateColumns: columnTemplate }}>
                    {calendarSettings.showWeekNumbers && <div className={`p-2 text-center text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>WK</div>}
                    {dayNames.map((name) => <div key={name} className={`p-2 text-center text-xs font-semibold ${isDark ? 'text-gray-200' : ''}`}>{name}</div>)}
                </div>
                {weeks.map((week) => <div key={dateKey(week[0])} className="grid" style={{ gridTemplateColumns: columnTemplate }}>
                    {calendarSettings.showWeekNumbers && <div className={`border-r border-b p-2 text-center text-xs ${isDark ? 'border-gray-700 text-gray-500' : 'text-gray-500'}`}>{getWeekNumber(week[0])}</div>}
                    {week.map((day) => {
                        const dayEvents = visible.filter((item) => sameDay(new Date(item.scheduled_start), day));
                        const inMonth = day.getMonth() === currentDate.getMonth();
                        return <div key={dateKey(day)} role={canManage ? 'button' : undefined} tabIndex={canManage ? 0 : undefined} onClick={canManage ? () => openEngagementForm(day, null, entity !== 'all' ? entity : null) : undefined} onKeyDown={canManage ? (e) => e.key === 'Enter' && openEngagementForm(day, null, entity !== 'all' ? entity : null) : undefined} className={`min-h-28 border-r border-b p-2 text-left ${canManage ? 'cursor-pointer' : ''} ${isDark ? 'border-gray-700 hover:bg-teal-900/30' : 'hover:bg-teal-50'} ${inMonth ? (isDark ? 'text-gray-100' : '') : (isDark ? 'bg-gray-900 text-gray-600' : 'bg-gray-50 text-gray-400')}`}>
                            <button type="button" onClick={(e) => { e.stopPropagation(); setCurrentDate(day); setView('day'); }} className={`mb-1 text-sm font-semibold hover:underline ${isDark ? 'text-gray-100' : ''}`}>{day.getDate()}</button>
                            <div className="space-y-1">{dayEvents.slice(0, 3).map((item) => <ScheduleBlock key={item.schedule_id} item={item} compact />)}
                                {dayEvents.length > 3 && <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>+{dayEvents.length - 3} more</div>}
                            </div>
                        </div>;
                    })}
                </div>)}
            </div>
        </div></div>;
    };

    const Agenda = () => <div className={`${TILE} ia-tile3d--clip pt-[5px]`}>
        {visible.filter((item) => new Date(item.scheduled_end) >= new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())).map((item) => {
            const start = new Date(item.scheduled_start);
            const end = new Date(item.scheduled_end);
            return <div key={item.schedule_id} role={phone || touch ? "button" : undefined} tabIndex={phone || touch ? 0 : undefined} onClick={phone || touch ? () => setSelectedEvent(item) : undefined} onKeyDown={phone || touch ? (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelectedEvent(item); } } : undefined} className={`ia-schedule-agenda-event flex flex-wrap items-center justify-between gap-3 border-b p-4 last:border-b-0 ${isDark ? 'border-gray-700' : ''}`} style={scheduleStyle(item)}>
                <div><div className="font-semibold">{item.schedule_type === 'block_off' && '🚫 '}{scheduleTitle(item)}</div>
                    <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{formatDateTime(start)}–{formatTime(end)} · {driverLabel(driversById.get(item.driver_id))}</div></div>
                <div className="flex gap-2"><span className="text-sm capitalize">{item.status}</span>{isConflict(item) && <span className={`rounded px-2 py-1 text-xs font-semibold ${isDark ? 'bg-red-900/60 text-red-300' : 'bg-red-100 text-red-700'}`}>Conflict</span>}</div>
            </div>;
        })}
        {visible.filter((item) => new Date(item.scheduled_end) >= new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())).length === 0 && <div className={`p-8 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No upcoming schedule records match these filters.</div>}
    </div>;

    const weekDays = Array.from({ length: 7 }, (_, index) => addDays(startOfWeek(currentDate, calendarSettings.firstDayOfWeek), index));

    return <div className={`ia-schedule max-w-7xl mx-auto ${isDark ? "ia-schedule-dark" : ""}`}>
        <ResponsiveRow className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div><h2 className={`text-2xl font-bold ${'text-[var(--ink)]'}`}>{scheduleCalendarLabel}</h2><p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Dispatch view of driver time and external busy context.</p></div>
            <ResponsiveRow className="flex flex-wrap gap-2" role="group" aria-label="Calendar view">{['month', 'week', 'day', 'agenda'].map((name) => <button key={name} type="button" aria-pressed={view === name} onClick={() => setView(name)} className={`${standardPillClass(view === name)} capitalize`}>{name}</button>)}</ResponsiveRow>
        </ResponsiveRow>
        <ResponsiveRow className="mb-4 flex flex-wrap items-center gap-3">
            <select value={driver} onChange={(event) => setDriver(event.target.value)} className={`border rounded px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-800 text-gray-100' : ''}`}><option value="all">All drivers</option>{drivers.map((item) => <option key={item.id} value={item.id}>{driverLabel(item)}</option>)}</select>
            <select value={entity} onChange={(event) => setEntity(event.target.value)} className={`border rounded px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-800 text-gray-100' : ''}`}><option value="all">All entities</option>{entities.map((item) => <option key={item.id} value={item.id}>{item.entity_code || item.name}</option>)}</select>
            <span className={`rounded px-3 py-2 text-sm ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100'}`}>Color by: {calendarSettings.colorBy === 'service' ? 'service' : 'staff'}</span>
        </ResponsiveRow>
        <ResponsiveRow className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <ResponsiveRow className="flex items-center gap-2"><button onClick={() => navigate(-1)} type="button" className={BTN}>Previous</button><button onClick={() => setCurrentDate(new Date())} type="button" className={BTN}>Today</button><button onClick={() => navigate(1)} type="button" className={BTN}>Next</button><span className={`font-medium ${'text-[var(--ink)]'}`}>{dateRangeLabel}</span></ResponsiveRow>
            {!accessLoading && canManage && <button type="button" onClick={() => setShowCreate(true)} className={BTN}>+ Block Off Time</button>}
        </ResponsiveRow>
        {error ? <div className="text-red-700">{error}</div> : <div>
            {view === 'month' && <MonthGrid />}
            {/* UX pass: called as a function (not <TimeGrid />) so a re-render keeps the same scroll box and its scroll position. */}
            {view === 'week' && TimeGrid({ days: weekDays })}
            {view === 'day' && TimeGrid({ days: [new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())] })}
            {view === 'agenda' && <Agenda />}
        </div>}
        {selectedEvent && <ModalSurface label="Schedule details" onClose={() => setSelectedEvent(null)} panelClassName={`ia-schedule-details rounded p-6 ${isDark ? 'bg-gray-800 text-gray-100' : 'bg-white'}`}>
            <div className="ia-modal-layout">
            <div className="ia-modal-body space-y-4">
                <h3 className="text-xl font-bold">{scheduleTitle(selectedEvent)}</h3>
                <dl className="space-y-3">
                    <div><dt>Time</dt><dd>{formatDateTime(new Date(selectedEvent.scheduled_start))}–{formatDateTime(new Date(selectedEvent.scheduled_end))}</dd></div>
                    <div><dt>Driver</dt><dd>{driverLabel(driversById.get(selectedEvent.driver_id))}</dd></div>
                    <div><dt>Status</dt><dd>{selectedEvent.status}{isConflict(selectedEvent) ? ' · Conflict' : ''}</dd></div>
                    {selectedEvent.confirmation_number && <div><dt>Confirmation #</dt><dd>{selectedEvent.confirmation_number}</dd></div>}
                    {selectedEvent.notes && <div><dt>Staff notes</dt><dd>{selectedEvent.notes}</dd></div>}
                    {selectedFlight && <div><dt className="flex flex-wrap items-center gap-2">Flight <HoverHelp helpId="schedule.flight-suggestion" userId={userId} content={{ title: 'Flight pickup suggestions', body: 'A flight change is a suggestion. Apply moves the pickup to the suggested time. Keep current pickup leaves the pickup time as it is. Only staff with dispatch edit access can choose.' }} /></dt><dd><FlightSuggestionCard detail={selectedFlight} client={supabaseClient} canEdit={canManage}
                        onChanged={async () => { await loadSelectedFlight(); await load(); setSelectedEvent(null); }} /></dd></div>}
                </dl>
            </div>
            <div className="ia-modal-actions"><button type="button" className={BTN} onClick={() => setSelectedEvent(null)}>Close</button></div>
            </div>
        </ModalSurface>}
        {showCreate && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"><div className={`w-full max-w-lg space-y-4 rounded p-6 ${isDark ? 'bg-gray-800 text-gray-100' : 'bg-white'}`}>
            <h3 className="text-xl font-bold">Block Off Time</h3>
            <select value={form.schedule_type} onChange={(event) => setForm({ ...form, schedule_type: event.target.value, confirmation_number: '' })} className={`w-full border p-2 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : ''}`}><option value="booking">Booking</option><option value="block_off">Block Off Time</option></select>
            <select value={form.driver_id} onChange={(event) => setForm({ ...form, driver_id: event.target.value })} className={`w-full border p-2 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : ''}`}><option value="">Select driver</option>{drivers.map((item) => <option key={item.id} value={item.id}>{driverLabel(item)}</option>)}</select>
            {form.schedule_type === 'booking' && <select value={form.confirmation_number} onChange={(event) => setForm({ ...form, confirmation_number: event.target.value })} className={`w-full border p-2 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : ''}`}><option value="">Select confirmed engagement</option>{engagements.map((item) => <option key={item.confirmation_number} value={item.confirmation_number}>{item.confirmation_number} — {item.service_type}</option>)}</select>}
            <input type="datetime-local" value={form.scheduled_start} onChange={(event) => setForm({ ...form, scheduled_start: event.target.value })} className={`w-full border p-2 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : ''}`} />
            <input type="datetime-local" value={form.scheduled_end} onChange={(event) => setForm({ ...form, scheduled_end: event.target.value })} className={`w-full border p-2 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : ''}`} />
            <textarea placeholder="Staff notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} className={`w-full border p-2 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-400' : ''}`} />
            <div className="flex justify-end gap-2"><button type="button" onClick={() => setShowCreate(false)} className={BTN}>Cancel</button><button type="button" onClick={createSchedule} className={BTN}>Create held window</button></div>
        </div></div>}
        {showEngagementForm && (
            <EngagementFormModal
                mode="create"
                entities={entities}
                showTripPrices={canShowTripPrices}
                onClose={() => setShowEngagementForm(false)}
                onSave={createEngagementFromCalendar}
                theme={theme}
                initialDate={engagementFormPrefill.initialDate}
                initialTime={engagementFormPrefill.initialTime}
                initialEntityId={engagementFormPrefill.initialEntityId}
            />
        )}
    </div>;
}

export default ScheduleCalendarView;
